import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Edit2, Check, X } from 'lucide-react';

export default function EditableBlock({ block, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(block?.content);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block?.id });

  const style = {
    transform: CSS?.Transform?.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    onUpdate(block?.id, editContent);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(block?.content);
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative border border-gray-200 rounded-lg hover:border-indigo-500 transition-all bg-white"
    >
      {/* Drag Handle and Actions */}
      <div className="absolute -left-10 top-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          {...attributes}
          {...listeners}
          className="p-1 bg-white border border-gray-300 rounded hover:bg-gray-50 cursor-move"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </button>
        <button
          onClick={() => onDelete(block?.id)}
          className="p-1 bg-white border border-gray-300 rounded hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4 text-red-600" />
        </button>
      </div>
      {/* Block Content */}
      <div className="p-4">
        {isEditing ? (
          <div>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e?.target?.value)}
              className="w-full min-h-[100px] p-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Edit HTML content..."
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
              >
                <Check className="w-4 h-4" />
                Save
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div
              dangerouslySetInnerHTML={{ __html: block?.content }}
              className="prose prose-sm max-w-none"
            />
            <button
              onClick={() => setIsEditing(true)}
              className="absolute top-0 right-0 p-1 bg-white border border-gray-300 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
            >
              <Edit2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}
      </div>
      {/* Block Type Indicator */}
      <div className="absolute top-2 left-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity">
        {block?.type}
      </div>
    </div>
  );
}